import React, { createContext, useContext, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Lets a drawer's content put its primary action ("Log time", "Print") in the drawer header
 * without the drawer host having to know about it. The host renders `slot` inside `extra` and
 * wraps its children in the provider; the content wraps its button in DrawerHeaderAction.
 *
 * `undefined` = no host slot at all (render the action inline where it is); `null` = the host
 * has a slot that is not mounted yet (render nothing for that frame rather than flashing inline).
 */
const DrawerActionsContext = createContext<HTMLElement | null | undefined>(undefined);

export const DrawerActionsProvider = DrawerActionsContext.Provider;

export const useDrawerActionsSlot = () => {
  const [el, setEl] = useState<HTMLElement | null>(null);
  const slot = <span ref={setEl} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }} />;
  return { el, slot };
};

interface IDrawerHeaderActionProps {
  /** The bare action, as it should sit in a drawer header. */
  children: React.ReactNode;
  /** What to render when there is no drawer header to go to (defaults to the children as they are). */
  inline?: React.ReactNode;
}

export const DrawerHeaderAction: React.FC<IDrawerHeaderActionProps> = ({ children, inline }) => {
  const el = useContext(DrawerActionsContext);
  if (el === undefined) return <>{inline ?? children}</>;
  return el ? createPortal(children, el) : null;
};
